/**
 * Distance units.
 *
 * The API stores metres everywhere. The watch displays yards with no toggle, so yards is
 * the default here for parity (canvas 1a: "Unidade padrão jardas"). The preference lives
 * in the browser only — there is no field on the user document to sync it, and the canvas
 * says so out loud in the Preferences row.
 */

import { intlTag, t } from '@/lib/i18n';
import { EM_DASH } from '@/lib/format/number';

export type DistanceUnit = 'yd' | 'm';

export const DEFAULT_UNIT: DistanceUnit = 'yd';

/** Same conversion and truncation as the watch, so the two apps never disagree by a yard. */
export function metersToYards(meters: number): number {
  return Math.trunc(meters * 1.09361);
}

export function yardsToMeters(yards: number): number {
  return Math.round(yards / 1.09361);
}

/** Converts a metre value from the API into the user's unit, as a whole number. */
export function toUnit(meters: number, unit: DistanceUnit): number {
  return unit === 'yd' ? metersToYards(meters) : Math.trunc(meters);
}

/**
 * O rótulo é **tradução**, não unidade. O tipo já separa a máquina do humano: `'yd'` é o símbolo
 * internacional e `'jd'` a abreviação de *jarda*. Já `m` é símbolo do SI e não muda de idioma.
 */
export function unitLabel(unit: DistanceUnit): string {
  return unit === 'yd' ? t('format.unit.yd') : t('format.unit.m');
}

/** "418 jd" — a distance with its unit. */
export function fmtDistance(meters: number | null | undefined, unit: DistanceUnit): string {
  if (meters == null) return EM_DASH;
  return `${integer.format(toUnit(meters, unit))} ${unitLabel(unit)}`;
}

/**
 * Distance to a course, for the Campos list. Kilometres with one decimal below 10 km,
 * whole kilometres above — "0,8 km" is useful, "341,2 km" is noise.
 */
export function fmtCourseDistance(meters: number): string {
  const km = meters / 1000;
  const value = km < 10 ? oneDecimal.format(km) : integer.format(Math.round(km));
  return `${value} ${t('format.unit.km')}`;
}

// Memoizados no topo, como em `format/number.ts`: um idioma por vida de página. Antes eram
// `toLocaleString` construídos a cada chamada, dentro de laços de lista.
const integer = new Intl.NumberFormat(intlTag);
const oneDecimal = new Intl.NumberFormat(intlTag, { maximumFractionDigits: 1 });

const UNIT_KEY = 'mrf.unit';

export function loadUnit(): DistanceUnit {
  try {
    const raw = localStorage.getItem(UNIT_KEY);
    return raw === 'm' || raw === 'yd' ? raw : DEFAULT_UNIT;
  } catch {
    return DEFAULT_UNIT;
  }
}

export function saveUnit(unit: DistanceUnit): void {
  try {
    localStorage.setItem(UNIT_KEY, unit);
  } catch {
    /* preference is best-effort */
  }
}
